import styles from "../article.module.scss"
import axios from "axios"
import Image from "next/image"
import StyledText from "../../../src/typography/typography-component"
import Button from "../../../src/components/Button/Button"
import Breadcrumb from "../../../src/ratgeber/breadcrumb"
import skColors from "../../../styles/skColors"
import { RatgeberArticlePage, RatgeberArticlePageWithLocalImage } from "../../../src/ratgeber/_types"
import DOMPurify from "isomorphic-dompurify"
import BehindNavBar from "../../../src/components/BehindNavBar/BehindNavBar"
import classNames from "classnames"
import React from "react"
import Link from "next/link"
import Footer from "../../../src/components/footer"
import * as Sentry from "@sentry/nextjs"
import SkinuvitaSeoHead from "../../../src/components/LandingPage/SkinuvitaSeoHead"
import { articleSchema, breadcrumbSchema, faqPageSchema } from "../../../src/seo/jsonLd"
import { DEFAULT_OG_IMAGE, SITE_URL, buildCanonicalUrl } from "../../../src/seo/siteConfig"
import { fetchAndCacheImage } from "../../../src/utils/fetchAndCacheImages"
import NavbarQuickfix from "../../../src/components/LandingPage/navbars/NavBarTranslationsQuickFix"
import { TrackedActions } from "../../../src/tracking/trackedActions"
import { calculateReadTime } from "../../../src/ratgeber/calculate_readtime"
import { parseRichTextHtml, type RichTextHeading } from "../../../src/utils/parseRichTextHtml"
import FAQ from "../../../src/content/faq/faq"
import { slugMatches } from "../../../src/utils/blogBuildingUtils"

const buildArticleContent = (
  body: RatgeberArticlePageWithLocalImage["body"]
): { headers: RichTextHeading[]; renderedBody: React.ReactNode[] } => {
  const headers: RichTextHeading[] = []
  const renderedBody: React.ReactNode[] = []
  const slugCounts = new Map<string, number>()

  body?.forEach((item) => {
    if (item.type === "richtext") {
      const { headers: richTextHeaders, content } = parseRichTextHtml(item.value, {
        collectHeadings: true,
        slugCounts,
        imageContainerClassName: styles.imageContainer,
        portraitImageContainerClassName: styles.imageContainerPortrait,
      })

      headers.push(...richTextHeaders)
      renderedBody.push(<React.Fragment key={item.id}>{content}</React.Fragment>)
      return
    }

    if (item.type === "cta") {
      if (item.value.description) {
        renderedBody.push(
          <div key={item.id} className={styles.ctaContainer}>
            <StyledText variant={"pLarge"}>{item.value.description}</StyledText>
            <Button
              className={styles.learnMoreButtons}
              style={{ width: "100%" }}
              variant={"CTA"}
              onClick={() => window.open(item.value.url, "_blank")}
            >
              <StyledText color={"white"} variant={"cta"}>{item.value.text}</StyledText>
            </Button>
            {item.value.secondary_button?.text && item.value.secondary_button?.url && (
              <Button
                style={{ width: "100%" }}
                variant={"secondaryCTA"}
                onClick={() => window.open(item.value.secondary_button.url, "_blank")}
              >
                <StyledText variant={"cta"}>{item.value.secondary_button.text}</StyledText>
              </Button>
            )}
          </div>
        )
        return
      }

      renderedBody.push(
        <div key={item.id} className={styles.ctaContainerWithoutDescription}>
          <Button style={{ width: "100%" }} variant={"CTA"} onClick={() => window.open(item.value.url, "_blank")}>
            {item.value.text}
          </Button>
          {item.value.secondary_button?.text && item.value.secondary_button?.url && (
            <Button
              style={{ width: "100%" }}
              variant={"secondaryCTA"}
              onClick={() => window.open(item.value.secondary_button.url, "_blank")}
            >
              <StyledText variant={"cta"}>{item.value.secondary_button.text}</StyledText>
            </Button>
          )}
        </div>
      )
    }
  })

  return { headers, renderedBody }
}


function AnimatedDisclosure({
  summary,
  children,
  className,
  defaultOpen = false,
}: {
  summary: React.ReactNode
  children: React.ReactNode
  className?: string
  defaultOpen?: boolean
}) {
  const [isOpen, setIsOpen] = React.useState(defaultOpen)
  const panelId = React.useId()

  return (
    <div className={classNames(styles.disclosure, className)}>
      <div
        role="button"
        tabIndex={0}
        className={classNames(styles.disclosureButton)}
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => setIsOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            setIsOpen((current) => !current)
          }
        }}
      >
        <div className={styles.disclosureSummaryContent}>{summary}</div>
        <Image
          src="/svg/chevron_down.svg"
          alt=""
          width={16}
          height={16}
          className={classNames(styles.disclosureIcon, isOpen && styles.rotated)}
        />
      </div>
      <div id={panelId} className={classNames(styles.disclosurePanel, isOpen && styles.disclosurePanelOpen)}>
        <div className={classNames(styles.disclosurePanelInner)}>{children}</div>
      </div>
    </div>
  )
}

export default function ArticlePost({ post }: { post: RatgeberArticlePageWithLocalImage }) {
  const title = post?.title
  const body = post?.body
  const articleRef = React.useRef<HTMLDivElement | null>(null)
  const [activeHeaderIds, setActiveHeaderIds] = React.useState<string[]>([])
  const [isDesktopArticleScroller, setIsDesktopArticleScroller] = React.useState(false)
  const { headers, renderedBody } = React.useMemo(() => buildArticleContent(body), [body])

  React.useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 1504px)")
    const updateLayoutMode = (event?: MediaQueryListEvent) => {
      setIsDesktopArticleScroller(event ? event.matches : mediaQuery.matches)
    }

    updateLayoutMode()
    mediaQuery.addEventListener("change", updateLayoutMode)

    return () => mediaQuery.removeEventListener("change", updateLayoutMode)
  }, [])

  React.useEffect(() => {
    if (headers.length === 0) {
      setActiveHeaderIds([])
      return
    }

    const observedHeadings = headers
      .map((header) => document.getElementById(header.id))
      .filter((heading): heading is HTMLElement => heading instanceof HTMLElement)

    if (observedHeadings.length === 0) {
      setActiveHeaderIds([])
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        setActiveHeaderIds((currentIds) => {
          const nextIds = new Set(currentIds)

          entries.forEach((entry) => {
            const id = entry.target.getAttribute("id")
            if (!id) {
              return
            }

            if (entry.intersectionRatio > 0) {
              nextIds.add(id)
            } else {
              nextIds.delete(id)
            }
          })

          return headers
            .map((header) => header.id)
            .filter((id) => nextIds.has(id))
        })
      },
      {
        root: isDesktopArticleScroller ? articleRef.current : null,
        rootMargin: "0px 0px -70% 0px",
        threshold: [0, 1],
      }
    )

    observedHeadings.forEach((heading) => observer.observe(heading))

    return () => observer.disconnect()
  }, [headers, isDesktopArticleScroller])

  const sanitizedAuthorAbout = !!post?.authors[0]?.about && DOMPurify.sanitize(post.authors[0].about)
  const sanitizedFactCheck = !!post?.fact_check && DOMPurify.sanitize(post.fact_check)

  const renderTocNavigation = (includeHeading = false) =>
    headers.length > 0 ? (
      <nav aria-label={"Inhaltsverzeichnis"}>
        {includeHeading && <h2 className={styles.tocHeading}>Inhaltsverzeichnis</h2>}
        <ul className={styles.tocList}>
          {headers.map((header) => (
            <li
              key={header.id}
              className={classNames(
                activeHeaderIds.includes(header.id) && styles.tocItemActive
              )}
            >
              <a
                href={`#${header.id}`}
                onClick={(event) => {
                  if (!isDesktopArticleScroller) {
                    return
                  }

                  const targetHeading = document.getElementById(header.id)
                  if (!(targetHeading instanceof HTMLElement)) {
                    return
                  }

                  event.preventDefault()
                  targetHeading.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  })
                  window.history.replaceState(null, "", `#${header.id}`)
                }}
              >
                {header.text}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    ) : null

  const readTime = calculateReadTime(renderedBody)

  const articleCanonicalUrl = buildCanonicalUrl(`/ratgeber/${post?.category_page?.slug}/${post?.meta?.slug}`)
  // The CMS banner is either a remote image object or an already-cached local path.
  // og:image and schema.org image both need an absolute URL.
  const rawBannerUrl =
    typeof post?.banner_image === "string" ? post.banner_image : post?.banner_image?.local_url
  const bannerImageUrl = rawBannerUrl
    ? rawBannerUrl.startsWith("http")
      ? rawBannerUrl
      : `${SITE_URL}${rawBannerUrl.startsWith("/") ? "" : "/"}${rawBannerUrl}`
    : DEFAULT_OG_IMAGE

  return (
    <>
      <SkinuvitaSeoHead
        title={post?.title}
        description={post?.description}
        canonicalPath={`/ratgeber/${post?.category_page?.slug}/${post?.meta?.slug}`}
        ogType={"article"}
        image={bannerImageUrl}
        jsonLd={[
          articleSchema({
            url: articleCanonicalUrl,
            headline: post?.title,
            description: post?.description,
            image: bannerImageUrl,
            datePublished: post?.date || post?.meta?.first_published_at,
            dateModified: post?.date || post?.meta?.first_published_at,
            authorName: post?.authors?.map((author) => author.name).join(", ") || undefined,
            section: post?.category_page?.title,
          }),
          breadcrumbSchema([
            { name: "Startseite", url: buildCanonicalUrl("/") },
            { name: "Ratgeber", url: buildCanonicalUrl("/ratgeber") },
            { name: post?.category_page?.title, url: buildCanonicalUrl(`/ratgeber/${post?.category_page?.slug}`) },
            { name: post?.title, url: articleCanonicalUrl },
          ]),
          faqPageSchema((post?.faqs || []).map((faq) => ({ question: faq.value.question, answer: faq.value.answer }))),
        ]}
      />
      <NavbarQuickfix />
      <div>
        <BehindNavBar />
        <div className={styles.articleLayout}>
          {headers.length > 0 && (
            <aside className={styles.tocSidebar} aria-label={"Inhaltsverzeichnis"}>
              <div className={styles.tocSidebarInner}>
                <div className={styles.tocSidebarContent}>{renderTocNavigation()}</div>
              </div>
            </aside>
          )}
          <div ref={articleRef} className={styles.articleScrollArea}>
            <article className={styles.article}>
              <Breadcrumb className={styles.breadcrumbMargin} page={post} />
              <StyledText variant={"h1"}>{title}</StyledText>

              <section className={styles.metaInformation}>
                <StyledText variant={"pSmall"} color={skColors.darkgreen[500]}>
                  {readTime > 0 && `Lesezeit: ${readTime} min`}
                </StyledText>
                <StyledText variant={"pSmall"} color={skColors.darkgreen[500]}>
                  {(post?.authors && post.authors.length > 1) || post?.authors[0]?.name.includes("&")
                    ? `Autor*innen: ${post?.authors?.map((author) => author.name).join(", ") || "No authors available"}`
                    : post?.authors && post.authors.length === 1
                    ? `Autor*in: ${post?.authors?.map((author) => author.name).join(", ") || "No authors available"}`
                    : null}
                </StyledText>
                <StyledText variant={"pSmall"} color={skColors.darkgreen[500]}>
                  {!!post?.date &&
                    `Stand: ${new Date(post.date).toLocaleDateString("de-DE", {
                      year: "numeric",
                      month: "long",
                      day: "2-digit",
                    })}`}
                </StyledText>
              </section>
              {headers.length > 0 && (
                <AnimatedDisclosure
                  className={styles.mobileToc}
                  summary={<StyledText variant={"h5"}>Inhaltsverzeichnis</StyledText>}
                  defaultOpen
                >
                  {renderTocNavigation()}
                </AnimatedDisclosure>
              )}

              <div className={styles.headerImageContainer}>
                <Image
                  src={post.banner_image.local_url}
                  alt={post.banner_image.title}
                  fill
                  sizes="100vw"
                  style={{
                    borderRadius: "12px",
                    objectFit: "cover",
                  }}
                />
              </div>
              <section className={styles.body}>{renderedBody}</section>
            </article>
          </div>
        </div>
        {post?.faqs && post.faqs.length > 0 &&
          <section className={styles.faqsSection}>
            <FAQ faqs={post?.faqs} />
          </section>
        }
        {post?.authors.length > 0 && (
          <section className={styles.authorSection}>
            <div className={styles.authorImageContainer}>
              {post?.authors[0]?.author_image?.url && (
                <Image
                  src={`${process.env.NEXT_PUBLIC_CONTENT_URL}${post?.authors[0]?.author_image?.url}`}
                  alt={post.authors[0].name}
                  unoptimized
                  fill
                  style={{
                    borderRadius: "12px",
                    objectFit: "contain",
                  }}
                />
              )}
            </div>
            {/*<Text variant={"h2"}>Über den Autor</Text>*/}
            <div className={styles.authorTextContainer}>
              <StyledText variant={"h2"}>{`Über ${post.authors[0].name}`}</StyledText>
              {!!post?.authors[0]?.about && (
                <div
                  className={classNames(styles.authorAbout, styles.sanitized)}
                  dangerouslySetInnerHTML={{ __html: sanitizedAuthorAbout }}
                />
              )}{" "}
              {!!post?.fact_check && (
                <div className={styles.factCheck}>
                  <StyledText variant={"h5"}>Faktencheck</StyledText>
                  <div
                    className={classNames(styles.sanitized, styles.factCheckText)}
                    dangerouslySetInnerHTML={{ __html: sanitizedFactCheck }}
                  />
                </div>
              )}
            </div>
          </section>
        )}
        <section className={styles.learnMore}>
          <StyledText variant={"h2"}>Skinuvita - Lichttherapie bequem von zuhause</StyledText>
          <div className={styles.learnMoreContent}>
            <div className={styles.learnMoreTextContainer}>
              <StyledText variant={"pLarge"}>
                <b>{`Du möchtest mehr über die Lichttherapie mit Skinuvita erfahren? `}</b>
                Stöbere auf unserer Website und entdecke mehr zu diesem Thema – oder finde mit unserem Skinuvita
                Selbst-Check heraus, ob Skinuvita eine Option für dich sein könnte!
              </StyledText>
              <div className={styles.learnMoreButtons}>
                <a style={{ width: "100%" }} onClick={() => void TrackedActions.goToSelfCheck()}>
                  <Button style={{ width: "100%" }} variant={"CTA"}>
                    <span>
                      <StyledText variant={"cta"} color={"white"}>
                        {`Ist Skinuvita `}
                      </StyledText>{" "}
                      <StyledText className={styles.hideWhenMobile} variant={"cta"} color={"white"}>
                        {`eine Option `}
                      </StyledText>
                      <StyledText className={styles.showWhenMobile} variant={"cta"} color={"white"}>
                        {`was `}
                      </StyledText>
                      <StyledText variant={"cta"} color={"white"}>
                        {`für mich? `}
                      </StyledText>
                    </span>
                  </Button>
                </a>
                <Link style={{ width: "100%" }} href={`/`}>
                  <Button style={{ width: "100%" }} variant={"secondaryCTA"}>
                    <StyledText variant={"cta"}>Zur Skinuvita Website</StyledText>
                  </Button>
                </Link>
              </div>
            </div>
            <div className={styles.learnMoreImageContainer}>
              <Image src={"/svg/home_therapy.svg"} alt={"learn more image"} fill style={{ objectFit: "cover" }} />
            </div>
          </div>
        </section>
        <Footer />
      </div>
    </>
  )
}

// Fetch all pages from paginated API endpoint
const fetchAllPages = async (url: string) => {
  const allItems: any[] = []
  let offset = 0
  const limit = 20
  let hasMore = true

  while (hasMore) {
    try {
      // Try with offset/limit first
      let fetchUrl = url
      if (url.includes("?")) {
        fetchUrl = `${url}&offset=${offset}&limit=${limit}`
      } else {
        fetchUrl = `${url}?offset=${offset}&limit=${limit}`
      }

      const res = await axios.get(fetchUrl)
      const items = res.data.items || []

      if (items.length === 0) {
        hasMore = false
      } else {
        allItems.push(...items)
        offset += items.length
        hasMore = items.length === limit
      }
    } catch (error: any) {
      console.warn(`Failed to fetch at offset ${offset}:`, error?.response?.status || error?.message)
      hasMore = false
    }
  }

  return allItems
}

// // This function gets called at build time
export async function getStaticPaths() {
  try {
    // Fetch both global articles and category-specific children (with pagination)
    const [globalArticles, categories] = await Promise.all([
      fetchAllPages(
        `${process.env.NEXT_PUBLIC_CONTENT_URL}/api/v2/pages/?type=ratgeber.RatgeberArticlePage&fields=*`
      ),
      fetchAllPages(
        `${process.env.NEXT_PUBLIC_CONTENT_URL}/api/v2/pages/?type=ratgeber.RatgeberCategoryPage&fields=*`
      ),
    ])

    const allArticles: any[] = [...globalArticles]
    const seenIds = new Set(globalArticles.map((a: any) => a.id))

    // Fetch category details and extract children
    const categoryDetailsPromises = categories.map((cat: any) =>
      axios.get(
        `${process.env.NEXT_PUBLIC_CONTENT_URL}/api/v2/pages/${cat.id}/?fields=*`
      ).catch(() => null)
    )
    const categoryDetails = await Promise.all(categoryDetailsPromises)

    // Add category children that aren't already in global articles
    for (const detail of categoryDetails) {
      if (!detail?.data?.children) continue
      const category = detail.data
      for (const child of detail.data.children) {
        if (!seenIds.has(child.id)) {
          // Add category_page info to child so it has routing info
          child.category_page = { slug: category.meta.slug }
          allArticles.push(child)
          seenIds.add(child.id)
        }
      }
    }

    const paths = allArticles
      .filter((post: any) => post.category_page?.slug && (post.meta?.slug || post.slug))
      .map((post: any) => ({
        params: {
          category: post.category_page.slug,
          slug: `${post.meta?.slug || post.slug}`,
        },
      }))
    console.log("Generated article paths:", paths.length)

    return {
      paths,
      fallback: "blocking",
    }
  } catch (error) {
    console.error("Failed to fetch article paths:", error)
    return {
      paths: [],
      fallback: "blocking",
    }
  }
}

export async function getStaticProps(context) {
  const routeSlug = String(context.params?.slug || "")
  const routeCategory = String(context.params?.category || "")

  try {
    // Fetch all articles with pagination
    const pages = await fetchAllPages(
      `${process.env.NEXT_PUBLIC_CONTENT_URL}/api/v2/pages/?type=ratgeber.RatgeberArticlePage&fields=*`
    )

    let matchedPage = pages.find((page: RatgeberArticlePage) => {
      const sameSlug = slugMatches(page?.meta?.slug || "", routeSlug)
      const sameCategory = slugMatches(page?.category_page?.slug || "", routeCategory)
      return sameSlug && sameCategory
    })

    // If not found in global list, try category children
    if (!matchedPage) {
      console.warn("Article not found in global list, checking category detail for:", routeSlug)
      try {
        const categories = await fetchAllPages(
          `${process.env.NEXT_PUBLIC_CONTENT_URL}/api/v2/pages/?type=ratgeber.RatgeberCategoryPage&fields=*`
        )
        const targetCategory = categories.find(
          (c: any) => slugMatches(c?.meta?.slug || "", routeCategory)
        )

        if (targetCategory) {
          const categoryDetailRes = await axios.get(
            `${process.env.NEXT_PUBLIC_CONTENT_URL}/api/v2/pages/${targetCategory.id}/?fields=*`
          )
          const children = categoryDetailRes.data.children || []
          matchedPage = children.find((child: any) =>
            slugMatches(child.slug || child.meta?.slug || "", routeSlug)
          )
        }
      } catch (categoryError) {
        console.warn("Failed to fetch from category detail:", categoryError)
      }
    }

    if (!matchedPage) {
      console.warn("No page found for slug:", context.params.slug)
      return { notFound: true }
    }

    const pageResponse = matchedPage

    let localImagePath: string | null = null
    if (pageResponse.banner_image?.full_url) {
      const filename = `${pageResponse.meta.slug}-${pageResponse.banner_image.id}.webp`
      try {
        localImagePath = await fetchAndCacheImage(pageResponse.banner_image.full_url, filename)
      } catch (imageError) {
        console.warn(`Failed to cache article image for ${pageResponse.meta.slug}`, imageError)
      }
    }
    const bannerImageSrc =
      localImagePath ||
      pageResponse.banner_image?.full_url ||
      `${process.env.NEXT_PUBLIC_CONTENT_URL}${pageResponse.banner_image?.url || ""}`

    const newPageResponse: RatgeberArticlePageWithLocalImage = {
      ...pageResponse,
      banner_image: {
        local_url: bannerImageSrc,
        title: pageResponse.banner_image.title,
      },
    }

    const locale = (context.locale as string) || "de"

    let messages
    try {
      messages = (await import(`../../../src/intl/${locale}.json`)).default
    } catch (err) {
      // fallback to default
      messages = (await import(`../../../src/intl/de.json`)).default
    }


    return {
      revalidate: 60 * 10, // 10 min
      props: {
        post: newPageResponse, // Artikeldaten
        error: null,
        messages,
        locale
      },
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Axios error:", error.message)
    } else {
      console.error("Unexpected error:", error)
    }

    Sentry.captureException(error)

    return {
      notFound: true,
    }
  }
}
